import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Input,
  Link,
  TextField,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { useCommunityDetails } from 'atoms/community-details';
import { httpClient } from '@cocoplatform/coco-rtc-client';
import CocoModal from 'components/coco-modal/coco-modal';
import FieldWithLabel from 'components/field-with-label/FieldWithLabel';
import ControlledTextField from 'components/forms/ControlledTextField';
import { useModal } from 'mui-modal-provider';
import React, { useState } from 'react';
import { i18n } from '@lingui/core';
import { useLingui } from '@lingui/react';
import { Trans, msg } from '@lingui/macro';

export enum BLOCK_TYPE {
  EDUCATIONAL_ONLY,
  PERSONAL_VIDEO_APPROVAL_REQUIRED,
  VIDEO_ACCESS_DISABLED,
  AUDIO_ACCESS_DISABLED,
  ALLOWED,
}

const getBlockTypeTranslated = (blockType: BLOCK_TYPE) =>
  ({
    [BLOCK_TYPE.EDUCATIONAL_ONLY]: i18n.t(
      'Disabled until educational account is approved.',
    ),
    [BLOCK_TYPE.PERSONAL_VIDEO_APPROVAL_REQUIRED]: i18n.t(
      'This feature is only available in educational communities or personal communities with video approval.',
    ),
    [BLOCK_TYPE.VIDEO_ACCESS_DISABLED]: i18n.t(
      'Video access is disabled for this community. Please contact community admin to request access.',
    ),
    [BLOCK_TYPE.AUDIO_ACCESS_DISABLED]: i18n.t(
      'Audio access is disabled for this community. Please contact community admin to request access.',
    ),
    [BLOCK_TYPE.ALLOWED]: '',
  })[blockType];

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

export function VideoFeatureRequestModal({
  onClose,
  communityId,
}: {
  onClose: () => void;
  communityId: string;
}) {
  const { _ } = useLingui();
  const [message, setMessage] = useState('');
  const [dispatching, setDispatching] = useState(false);

  const { patchCommunityDetails } = useCommunityDetails({
    communityId,
  });
  return (
    <CocoModal onClose={onClose} header={_(msg`Request Live Video Access`)}>
      <Box p={4}>
        <FieldWithLabel
          label={_(
            msg`Please share more information about yourself and how you are using CoCo. Add any website / social media link to help us verify your profile. If you are under 13 years old, please ask a parent / guardian to send us a note.`,
          )}
          name='request'
          field={
            <>
              <Box mt={2} />
              <TextField
                name='request'
                fullWidth
                variant='outlined'
                id='request'
                placeholder={_(
                  msg`Add a message here to help CoCo team review your request.....`,
                )}
                required
                sx={{
                  autoComplete: 'one-time-code',
                }}
                multiline
                minRows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </>
          }
        />
        <Box mt={2} />
        <LoadingButton
          onClick={async () => {
            setDispatching(true);
            await httpClient.post(
              `/community/${communityId}/request-video-access`,
              { message },
            );
            setDispatching(false);
            patchCommunityDetails((c) => {
              return {
                ...c,
                personalVideoAccessRequested: true,
              };
            });
            onClose();
          }}
          loading={dispatching}
          color='primary'
          variant='contained'
          fullWidth
        >
          <Trans>Submit</Trans>
        </LoadingButton>
      </Box>
    </CocoModal>
  );
}

export function VideoFeatureRequestAccess(p: {
  communityId?: string;
  disabled?: boolean;
}) {
  const { _ } = useLingui();
  const { showModal } = useModal();
  return (
    <>
      <Link
        title={
          p.disabled ? _(msg`You have already requested access`) : undefined
        }
        onClick={() => {
          if (p.disabled) return;
          const modal = showModal(VideoFeatureRequestModal, {
            onClose: () => {
              modal.destroy();
            },
            communityId: p.communityId!,
          });
        }}
        sx={{
          fontWeight: p.disabled ? undefined : 'var(--font-weight-bold)',
          textDecoration: 'underline !important',
          color: p.disabled ? 'var(--color-subtext)' : 'var(--color-primary)',
          cursor: p.disabled ? 'not-allowed' : 'pointer',
        }}
        color='primary'
      >
        <Trans>Request Access</Trans>
      </Link>
    </>
  );
}

export default function BlockedFeature({
  children,
  type,
}: {
  children: any;
  type: BLOCK_TYPE;
}) {
  const theme = useTheme();
  if (type === BLOCK_TYPE.ALLOWED) {
    return children;
  }
  return (
    <HtmlTooltip
      sx={{
        [`& .${tooltipClasses.tooltip}`]: {
          backgroundColor: '#f5f5f9',
          color: 'rgba(0, 0, 0, 0.87)',
          maxWidth: 220,
          fontSize: theme.typography.pxToRem(12),
          border: '1px solid #dadde9',
        },
      }}
      placement='top-start'
      title={
        <React.Fragment>
          <Typography variant='body1'>
            {getBlockTypeTranslated(type)}
          </Typography>
        </React.Fragment>
      }
    >
      <Box
        sx={{
          cursor: 'not-allowed',
          color: 'var(--color-subtext) !important',
        }}
      >
        {children}
      </Box>
    </HtmlTooltip>
  );
}
